import { useQuery } from "@tanstack/react-query";
import { getUserIdToken } from "../utils/auth";

// Base URL for the API
const API_BASE_URL = "https://app.mighty45.com/api/v1";

/**
 * Helper function to make authenticated API calls
 */
const makeApiCall = async (endpoint, options = {}) => {
  try {
    const token = await getUserIdToken();

    const defaultOptions = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...defaultOptions,
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
};

/**
 * Hook to fetch muscle groups (API: bundled static snapshot, not BigQuery)
 * Replaces Firebase GlobalData.getMuscleGroups() calls
 */
export const useMuscleGroupsBigQuery = () => {
  return useQuery({
    queryKey: ["muscle-groups-bigquery"],
    queryFn: async () => {
      const response = await makeApiCall("/muscle-groups-bigquery");
      return response.data;
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 hours - static data doesn't change often
    cacheTime: 1000 * 60 * 60 * 24 * 7, // 7 days
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // Fallback data when API fails
    placeholderData: [
      {
        id: "chest",
        data: () => ({
          id: "chest",
          name: "Chest",
          description: "Chest muscles",
          index: 0,
        }),
      },
      {
        id: "back",
        data: () => ({
          id: "back",
          name: "Back",
          description: "Back muscles",
          index: 1,
        }),
      },
      {
        id: "shoulders",
        data: () => ({
          id: "shoulders",
          name: "Shoulders",
          description: "Shoulder muscles",
          index: 2,
        }),
      },
      {
        id: "arms",
        data: () => ({
          id: "arms",
          name: "Arms",
          description: "Arm muscles",
          index: 3,
        }),
      },
      {
        id: "legs",
        data: () => ({
          id: "legs",
          name: "Legs",
          description: "Leg muscles",
          index: 4,
        }),
      },
      {
        id: "core",
        data: () => ({
          id: "core",
          name: "Core",
          description: "Core muscles",
          index: 5,
        }),
      },
      {
        id: "full_body",
        data: () => ({
          id: "full_body",
          name: "Full Body",
          description: "Full body exercises",
          index: 6,
        }),
      },
      {
        id: "cardio",
        data: () => ({
          id: "cardio",
          name: "Cardio",
          description: "Cardiovascular exercises",
          index: 7,
        }),
      },
      {
        id: "flexibility",
        data: () => ({
          id: "flexibility",
          name: "Flexibility",
          description: "Flexibility and mobility",
          index: 8,
        }),
      },
    ],
  });
};

/**
 * Hook to fetch equipment (API: bundled static snapshot, not BigQuery)
 * Replaces Firebase GlobalData.getEquipmentKey() calls
 */
export const useEquipmentBigQuery = () => {
  return useQuery({
    queryKey: ["equipment-bigquery"],
    queryFn: async () => {
      const response = await makeApiCall("/equipment-bigquery");
      return response.data;
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 hours - static data doesn't change often
    cacheTime: 1000 * 60 * 60 * 24 * 7, // 7 days
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // Fallback data when API fails
    placeholderData: [
      {
        id: "bodyweight",
        data: () => ({
          id: "bodyweight",
          name: "Bodyweight",
          description: "No equipment needed",
        }),
      },
      {
        id: "dumbbells",
        data: () => ({
          id: "dumbbells",
          name: "Dumbbells",
          description: "Dumbbell weights",
        }),
      },
      {
        id: "barbell",
        data: () => ({
          id: "barbell",
          name: "Barbell",
          description: "Barbell and plates",
        }),
      },
      {
        id: "resistance_bands",
        data: () => ({
          id: "resistance_bands",
          name: "Resistance Bands",
          description: "Elastic resistance bands",
        }),
      },
      {
        id: "pull_up_bar",
        data: () => ({
          id: "pull_up_bar",
          name: "Pull-up Bar",
          description: "Pull-up or chin-up bar",
        }),
      },
    ],
  });
};

/**
 * Hook to fetch difficulty levels (API: bundled static snapshot, not BigQuery)
 * Replaces Firebase GlobalData.getDifficultyKey() calls
 */
export const useDifficultyBigQuery = () => {
  return useQuery({
    queryKey: ["difficulty-bigquery"],
    queryFn: async () => {
      const response = await makeApiCall("/difficulty-bigquery");
      return response.data;
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 hours - static data doesn't change often
    cacheTime: 1000 * 60 * 60 * 24 * 7, // 7 days
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // Fallback data when API fails
    placeholderData: [
      {
        id: "beginner",
        data: () => ({
          id: "beginner",
          name: "Beginner",
          description: "Suitable for those new to exercise or returning after a break",
          sort_order: 1,
        }),
      },
      {
        id: "intermediate",
        data: () => ({
          id: "intermediate",
          name: "Intermediate",
          description: "For those with some exercise experience and basic fitness",
          sort_order: 2,
        }),
      },
      {
        id: "advanced",
        data: () => ({
          id: "advanced",
          name: "Advanced",
          description: "For experienced exercisers with good fitness levels",
          sort_order: 3,
        }),
      },
      {
        id: "expert",
        data: () => ({
          id: "expert",
          name: "Expert",
          description: "For highly experienced exercisers and fitness professionals",
          sort_order: 4,
        }),
      },
    ],
  });
};
